import { useCallback, useMemo, useState } from 'react';
import {
  Text,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Listbox,
  Option,
  Input,
} from '@fluentui/react-components';
import {
  DismissRegular,
  FilterDismissRegular,
  ChevronDownRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { iconSize } from '@/theme';
import type {
  FilterCategory,
  FilterPillData,
  OwnerOption,
  PlannerEventStatus,
} from '@/types/planner.types';
import { useEventFilterTagBarStyles } from './EventFilterTagBar.styles';

export interface EventFilterTagBarProps {
  pills: FilterPillData[];
  onRemove: (category: FilterCategory) => void;
  onClearAll: () => void;
  onUpdateFilter?: (category: FilterCategory, values: string[]) => void;
  onOpenPanelForDate?: (category: FilterCategory) => void;
  availableOwners?: OwnerOption[];
  availableStatuses?: PlannerEventStatus[];
  availableBuildings?: OwnerOption[];
  currentOwners?: string[];
  currentStatuses?: PlannerEventStatus[];
  currentBuildings?: string[];
  ownerIsLoading?: boolean;
  ownerErrorState?: 'NONE' | 'BUILDING_ACCESS' | 'PLANNERS_BY_BUILDING';
  onOwnerRetry?: () => void;
}

export const EventFilterTagBar = ({
  pills,
  onRemove,
  onClearAll,
  onUpdateFilter,
  onOpenPanelForDate,
  availableOwners = [],
  availableStatuses = [],
  availableBuildings = [],
  currentOwners = [],
  currentStatuses = [],
  currentBuildings = [],
  ownerIsLoading = false,
  ownerErrorState = 'NONE',
  onOwnerRetry,
}: EventFilterTagBarProps) => {
  const styles = useEventFilterTagBarStyles();
  const { t } = useTranslation('planner');
  const [openPopover, setOpenPopover] = useState<FilterCategory | null>(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [buildingSearch, setBuildingSearch] = useState('');

  const filteredOwners = useMemo(() => {
    const q = ownerSearch.trim().toLowerCase();
    if (q.length === 0) return availableOwners;
    return availableOwners.filter((o) => o.displayName.toLowerCase().includes(q));
  }, [availableOwners, ownerSearch]);

  const filteredStatuses = useMemo(() => {
    const q = statusSearch.trim().toLowerCase();
    if (q.length === 0) return availableStatuses;
    return availableStatuses.filter((s) => s.toLowerCase().includes(q));
  }, [availableStatuses, statusSearch]);

  const filteredBuildings = useMemo(() => {
    const q = buildingSearch.trim().toLowerCase();
    if (q.length === 0) return availableBuildings;
    return availableBuildings.filter((b) => b.displayName.toLowerCase().includes(q));
  }, [availableBuildings, buildingSearch]);

  const handlePillClick = useCallback(
    (category: FilterCategory) => {
      if (category === 'submittedDate' || category === 'eventDate') {
        onOpenPanelForDate?.(category);
      } else {
        setOpenPopover((prev) => (prev === category ? null : category));
      }
    },
    [onOpenPanelForDate]
  );

  const handleDismiss = useCallback(
    (e: React.MouseEvent, category: FilterCategory) => {
      e.stopPropagation();
      onRemove(category);
    },
    [onRemove]
  );

  const handleOwnerOptionSelect = useCallback(
    (_e: unknown, data: { selectedOptions: string[] }) => {
      onUpdateFilter?.('owner', data.selectedOptions);
      if (data.selectedOptions.length === 0) {
        setOpenPopover(null);
      }
    },
    [onUpdateFilter]
  );

  const handleStatusOptionSelect = useCallback(
    (_e: unknown, data: { selectedOptions: string[] }) => {
      onUpdateFilter?.('status', data.selectedOptions);
      if (data.selectedOptions.length === 0) {
        setOpenPopover(null);
      }
    },
    [onUpdateFilter]
  );

  const handleBuildingOptionSelect = useCallback(
    (_e: unknown, data: { selectedOptions: string[] }) => {
      onUpdateFilter?.('building', data.selectedOptions);
      if (data.selectedOptions.length === 0) {
        setOpenPopover(null);
      }
    },
    [onUpdateFilter]
  );

  if (pills.length === 0) {
    return null;
  }

  const renderInlineDropdown = (category: FilterCategory) => {
    if (category === 'owner') {
      if (ownerIsLoading) {
        return (
          <div data-testid="inline-owner-loading" className={styles.inlinePopoverMessage}>
            {t('filter.ownerLoading')}
          </div>
        );
      }
      if (ownerErrorState !== 'NONE') {
        return (
          <BasicButton
            variant="secondary"
            size="medium"
            onClick={onOwnerRetry}
            data-testid="inline-owner-retry"
          >
            {t('filter.ownerErrorRetry')}
          </BasicButton>
        );
      }
      if (availableOwners.length === 0) {
        return (
          <div data-testid="inline-owner-empty" className={styles.inlinePopoverMessage}>
            {t('filter.ownerEmpty')}
          </div>
        );
      }
      return (
        <div className={styles.inlineSearchContainer}>
          <Input
            size="small"
            placeholder={t('filter.searchOwners')}
            value={ownerSearch}
            onChange={(_, data) => setOwnerSearch(data.value)}
            contentBefore={<SearchRegular />}
            data-testid="inline-owner-search"
          />
          <Listbox
            multiselect
            selectedOptions={currentOwners}
            onOptionSelect={handleOwnerOptionSelect}
            data-testid="inline-owner-dropdown"
            className={styles.inlineListbox}
          >
            {filteredOwners.map((owner) => (
              <Option key={owner.id} value={owner.id} text={owner.displayName}>
                {owner.displayName}
              </Option>
            ))}
            {filteredOwners.length === 0 && (
              <Option key="__no_match__" value="__no_match__" disabled text="">
                {t('filter.ownerEmpty')}
              </Option>
            )}
          </Listbox>
        </div>
      );
    }
    if (category === 'status') {
      return (
        <div className={styles.inlineSearchContainer}>
          <Input
            size="small"
            placeholder={t('filter.searchStatuses')}
            value={statusSearch}
            onChange={(_, data) => setStatusSearch(data.value)}
            contentBefore={<SearchRegular />}
            data-testid="inline-status-search"
          />
          <Listbox
            multiselect
            selectedOptions={currentStatuses}
            onOptionSelect={handleStatusOptionSelect}
            data-testid="inline-status-dropdown"
            className={styles.inlineListbox}
          >
            {filteredStatuses.map((status) => (
              <Option key={status} value={status} text={status}>
                {status}
              </Option>
            ))}
            {filteredStatuses.length === 0 && (
              <Option key="__no_match__" value="__no_match__" disabled text="">
                {t('filter.noResults')}
              </Option>
            )}
          </Listbox>
        </div>
      );
    }
    if (category === 'building') {
      return (
        <div className={styles.inlineSearchContainer}>
          <Input
            size="small"
            placeholder={t('filter.searchBuildings')}
            value={buildingSearch}
            onChange={(_, data) => setBuildingSearch(data.value)}
            contentBefore={<SearchRegular />}
            data-testid="inline-building-search"
          />
          <Listbox
            multiselect
            selectedOptions={currentBuildings}
            onOptionSelect={handleBuildingOptionSelect}
            data-testid="inline-building-dropdown"
            className={styles.inlineListbox}
          >
            {filteredBuildings.map((building) => (
              <Option key={building.id} value={building.id} text={building.displayName}>
                {building.displayName}
              </Option>
            ))}
            {filteredBuildings.length === 0 && (
              <Option key="__no_match__" value="__no_match__" disabled text="">
                {t('filter.buildingEmpty')}
              </Option>
            )}
          </Listbox>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.root} data-testid="event-filter-tag-bar">
      <Text className={styles.filteredByLabel}>{t('filter.filteredBy')}</Text>
      <div className={styles.pillContainer}>
        {pills.map((pill) => {
          const isDropdownCategory =
            pill.category === 'owner' || pill.category === 'status' || pill.category === 'building';

          if (isDropdownCategory && onUpdateFilter) {
            return (
              <div
                key={pill.category}
                className={styles.pill}
                data-testid={`filter-pill-${pill.category}`}
              >
                <Popover
                  open={openPopover === pill.category}
                  onOpenChange={(_e, data) => {
                    if (!data.open) {
                      setOpenPopover(null);
                      setOwnerSearch('');
                      setStatusSearch('');
                      setBuildingSearch('');
                    }
                  }}
                >
                  <PopoverTrigger disableButtonEnhancement>
                    <button
                      className={styles.pillContent}
                      onClick={() => handlePillClick(pill.category)}
                      type="button"
                      data-testid={`filter-pill-content-${pill.category}`}
                    >
                      <Text className={styles.pillLabel}>{pill.label}</Text>
                      <Text className={styles.pillValue}>{pill.displayValue}</Text>
                      <ChevronDownRegular style={{ fontSize: iconSize(12) }} />
                    </button>
                  </PopoverTrigger>
                  <PopoverSurface data-testid={`inline-popover-${pill.category}`}>
                    {renderInlineDropdown(pill.category)}
                  </PopoverSurface>
                </Popover>
                <button
                  className={styles.pillDismiss}
                  onClick={(e) => handleDismiss(e, pill.category)}
                  type="button"
                  aria-label={`Remove ${pill.label} filter`}
                  data-testid={`filter-pill-dismiss-${pill.category}`}
                >
                  <DismissRegular style={{ fontSize: iconSize(12) }} />
                </button>
              </div>
            );
          }

          return (
            <div
              key={pill.category}
              className={styles.pill}
              data-testid={`filter-pill-${pill.category}`}
            >
              <button
                className={styles.pillContent}
                onClick={() => handlePillClick(pill.category)}
                type="button"
                data-testid={`filter-pill-content-${pill.category}`}
              >
                <Text className={styles.pillLabel}>{pill.label}</Text>
                <Text className={styles.pillValue}>{pill.displayValue}</Text>
              </button>
              <button
                className={styles.pillDismiss}
                onClick={(e) => handleDismiss(e, pill.category)}
                type="button"
                aria-label={`Remove ${pill.label} filter`}
                data-testid={`filter-pill-dismiss-${pill.category}`}
              >
                <DismissRegular style={{ fontSize: iconSize(12) }} />
              </button>
            </div>
          );
        })}
      </div>
      <BasicButton
        variant="subtle"
        size="medium"
        className={styles.clearAllButton}
        icon={<FilterDismissRegular />}
        onClick={onClearAll}
        data-testid="filter-clear-all"
      >
        {t('filter.clearAll')}
      </BasicButton>
    </div>
  );
};

EventFilterTagBar.displayName = 'EventFilterTagBar';
